/**
 * HustleStadiumSalesPage.jsx
 * ──────────────────────────
 * Sales page for HustleStadium — the food + drink + venue-map
 * operating layer for stadiums, arenas, theme parks, race tracks
 * and any large-scale ticketed venue.
 *
 * Value prop: Fans pre-order from stall menus, pick up on the
 * concourse (or claim seat-side delivery), and split-pay a group
 * order across four wallets. Operators get a live venue map, real
 * kitchen-side stall queues, and every transaction reconciled
 * against Stripe — no cash tin at the door.
 *
 * Rendered at:
 *   • https://hustlestadium.au/         (RootRouter → this page)
 *   • https://hustleplate.au/hustlestadium (SPA preview route)
 *
 * Aesthetic matches HustlePlateSalesPage & DirectorySalesPage
 * (cream, Georgia, amber-emerald accents, print-friendly).
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, MapPin, Users, Wallet, QrCode, Timer,
  UtensilsCrossed, BarChart3, Printer, Building2, ChefHat,
  Ticket, HandCoins, Sparkles, Award, Utensils,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FounderChatPill from "@/components/FounderChatPill";
import VipBookingWidget from "@/components/stadium/VipBookingWidget";
import StadiumWalletTopupWidget from "@/components/stadium/StadiumWalletTopupWidget";

const LOGO =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

const ctaPrimaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999, border: "none",
  background: "#c2410c", color: "#fff",
  fontWeight: 700, fontSize: 15, cursor: "pointer",
  boxShadow: "0 8px 20px rgba(194,65,12,.22)",
  textDecoration: "none",
};
const ctaSecondaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999,
  border: "1.5px solid #1a1408", color: "#1a1408",
  fontWeight: 700, fontSize: 15,
  background: "transparent", textDecoration: "none",
  cursor: "pointer",
};

// ── 6 core capabilities the platform ships with ─────────────────
const CAPS = [
  { icon: QrCode,          t: "Stall-menu QR codes",
    b: "Every food stall gets a printable QR that opens the whole venue menu at that stall's page. Fans can order without leaving their seat." },
  { icon: HandCoins,       t: "Prepay before the whistle",
    b: "Season-ticket holders pre-order for match day and pick up 15 minutes into halftime. Kitchen prepares ahead — zero queue, zero cold chips." },
  { icon: Users,           t: "Group split-pay (up to 12)",
    b: "One person picks the menu, everyone else settles their share in-app. Apple Pay, Google Pay, Stripe card, HustlePoints — mixed and matched." },
  { icon: MapPin,          t: "Live venue map",
    b: "Interactive stadium map shows which stalls have queues (green / amber / red), which are sold out of the good chips, and which are closest to your seat." },
  { icon: UtensilsCrossed, t: "Pickup OR seat-side",
    b: "Fans choose: walk to the stall (skip the queue with a QR pickup code) OR pay a small surcharge for a runner to deliver to their seat block." },
  { icon: BarChart3,       t: "Real-time ops dashboard",
    b: "Venue managers see live throughput by stall, average pickup time, most-ordered items, and the wallet-share split between HustlePoints and card." },
  { icon: Award,           t: "VIP dining + numbered viewing decks",
    b: "Book the event's VIP restaurant, corporate box or viewing deck with real table numbers and seat numbers — orders auto-route to the table, runners deliver seat-side, and every guest on the booking joins the split-pay tab." },
  { icon: Wallet,          t: "Wallet top-up at ticket checkout",
    b: "Bolt onto Ticketek, Ticketmaster, Moshtix or HustleTickets — fans pre-load their match-day wallet during ticket purchase, so gate-day ATM queues disappear and pre-orders start the moment the doors open." },
];

const TIERS = [
  {
    key: "free",
    name: "Match-day trial",
    price: "$0",
    period: "/1 event",
    blurb: "Try the whole stack on one game / show / festival day. Cancel any time — free forever if you never do a second event.",
    color: "#1f7a1f",
    cta: { label: "Book a demo match day", href: "/claim-business?product=stadium&tier=trial" },
    features: [
      "Full food-stall QR ordering",
      "Group split-pay up to 4 people",
      "Live venue map (auto-drawn from your seat plan)",
      "Standard payout (T+2 via Stripe)",
      "Email + Slack support",
      "Zero setup fee, zero card fee on trial",
    ],
  },
  {
    key: "featured",
    name: "HustlePlatinum — Stadium",
    price: "$1,490",
    period: "/mo",
    badge: "Recommended",
    color: "#b08a3e",
    highlight: true,
    blurb: "Everything a mid-size venue needs to run a full season. Includes the Featured badge across every HustleSuite surface (HustleDirectory, HustlePlaza, HustleTravel).",
    cta: { label: "Talk to us about Platinum", href: "/claim-business?product=stadium&tier=platinum" },
    features: [
      "Everything in the trial, plus:",
      "Unlimited events per month",
      "Group split-pay up to 12 people",
      "Seat-side runner scheduling (optional)",
      "Custom-branded fan mobile app",
      "AI menu-tune (bumps stall margins by ~9%)",
      "Same-day payout (T+0 via Stripe)",
      "Dedicated success manager",
      "PLATINUM badge in HustleDirectory",
      "Cross-listed on HustlePlaza + HustleTravel event feeds",
    ],
  },
  {
    key: "enterprise",
    name: "Season pass",
    price: "Contact us",
    period: "",
    color: "#4a3b1f",
    blurb: "For AFL / NRL / A-League / Big Bash-scale venues. Multi-tenant sub-accounts (one per stall operator), custom stripe payout schedule, and on-site engineer for the first three match days.",
    cta: { label: "Book a discovery call", href: "/claim-business?product=stadium&tier=enterprise" },
    features: [
      "Everything in Platinum, plus:",
      "Multi-tenant stall operator sub-accounts",
      "Custom Stripe Connect payout schedule",
      "On-site engineer for first 3 events",
      "Bespoke integration with your ticketing partner (Ticketek, Ticketmaster, Moshtix)",
      "24/7 phone support with a 15-minute SLA",
      "Annual master-services agreement + SOC 2 questionnaire pack",
    ],
  },
];

const HOW = [
  { n: "01", icon: MapPin,
    t: "We render your venue",
    b: "Send us the seat plan + a list of your stalls. We build the interactive venue map (same engine as SeatingMaker) in ~48h." },
  { n: "02", icon: ChefHat,
    t: "You upload the menu",
    b: "Import via HustlePlate Menu Builder, spreadsheet, or paste from Word. Allergen tags + photos are auto-generated by Gemini." },
  { n: "03", icon: Ticket,
    t: "Print QR codes + go live",
    b: "Each stall gets a printable QR pack + we mail you branded table talkers. Fans scan and order the moment the doors open." },
];

const FAQS = [
  { q: "How does season-lock payment work?",
    a: "When a venue enables 'Allow members to lock a VIP table for the whole season' in Ops → Settings, they set a Stripe deposit (default $200) and a per-match instalment (default $150). Fans clicking 'Lock this table for the season' on the sales-page widget are redirected to Stripe Checkout — the deposit is charged there and then. Stripe's checkout.session.completed webhook flips the lock from `pending_payment` to `active`. Per-match instalments are invoiced by the venue via Stripe Invoicing on match-day-eve — reconciled through the same Stripe Connect account as concourse orders, so you get one T+0 payout on Platinum. Set the deposit to $0 in Settings to keep the legacy inquiry-then-invoice flow instead.",
  },
  { q: "What happens when a fan returns their ticket?",
    a: "Ticketek, Ticketmaster, Moshtix and HustleTickets each POST a cancellation event to /api/stadium/checkout-bridge/webhook/{partner} with an HMAC signature. On a valid signature + matching order_ref, we auto-refund the linked wallet top-up via Stripe (the same charge the fan paid at ticket purchase). The bridge txn flips to `cancelled` with `refund_status: refunded`, and the runsheet strips the top-up from the match-day totals. Refund failures are logged with `refund_status: refund-failed` so ops managers can intervene manually.",
  },
  { q: "What stops a competitor spamming our VIP inquiry inbox?",
    a: "Every submission through the sales-page widget is rate-limited on the backend — 3 inquiries per email per hour and 10 per IP per hour (mongo-backed, so it works across pods). Legit fans never notice; competitors who script a flood get 429s and their submissions never reach your ops inbox. Emails to your ops team stay clean.",
  },
  { q: "Can we print a runsheet before doors open?",
    a: "Yes. In the ops console, hit the Runsheet tab, pick the match date and click Print. You get a single-page A4 runsheet with the VIP bookings, season-lock hospitality guests, and every confirmed wallet top-up for that match — including partner (Ticketek / Ticketmaster / Moshtix / HustleTickets), amount and credit issued. Ops managers usually run this 90 minutes before doors open and hand a hard copy to each VIP host station.",
  },
  { q: "How does the season-pass VIP table lock work?",
    a: "When a venue turns on 'Allow season-pass VIP table lock' in their ops console settings, a season-ticket holder can claim the same VIP table for every home game with one click. The lock reserves that table + its seats across the entire season, so corporate memberships and Platinum season-pass buyers pre-book the whole roster in a single transaction. Ops teams get emailed the lock request the same way every VIP inquiry is emailed (via Resend), and the season lock is stored in `hp_stadium_season_locks` so it appears in the ops dashboard analytics tab too. If your venue rotates VIP hospitality on a per-match auction, keep the setting off — the widget will still show single-match availability but hide the season-lock checkbox.",
  },
  { q: "Who can edit our venue in the ops console?",
    a: "Only the business admin whose email matches the venue's owner_email can edit label, ops emails, floor plan CSV, and settings. Agency-owner or sysadmin roles can edit any venue for support purposes. The first authed user to save a seeded venue (Suncorp Stadium, The Gabba) auto-claims ownership on their email. Every save is stamped in the audit log so there's never a doubt who changed what and when.",
  },
  { q: "How does the VIP restaurant + numbered viewing deck feature work?",
    a: "The VIP module is a first-class part of HustleStadium — not a bolt-on. Import your corporate box, trackside restaurant, rooftop bar or dress-circle balcony via HustlePlate's Table Builder (same engine our restaurant clients use). Every table gets a persistent number for the whole season, every viewing-deck seat gets an identifier, and each surface generates its own QR. Guests can scan the table QR to open a shared booking tab or scan their individual seat QR for a private tab. Runners deliver seat-side using the exact block-row-seat identifier — no more 'section 4, corner of the couch, second guy'. Every guest on the lead-booker's ticket is auto-invited into the split-pay group. Pre-order windows can be anchored to the match clock, the intermission bell or the encore start. VIP transactions settle on the same Stripe Connect payout schedule as your concourse stalls, so match-day reconciliation is a single line item on Tuesday morning. One platform, one wallet, one payout — for GA, VIP dining and numbered viewing decks alike." },
  { q: "How does wallet top-up at ticket checkout work with existing ticket rails?",
    a: "It's a checkout add-on module that we drop onto Ticketek, Ticketmaster, Moshtix and HustleTickets — the wallet load shows up as a line item on the ticket purchase, not a separate step. Charges post through the fan's original card via Stripe Connect (or via the ticket rail's payment processor when the rail insists), and the resulting balance lands in the HustleStadium wallet linked to their scanned ticket. Promo codes, per-fan match-day caps and RSA gates are configured in your operator console — you can tie the first-drink voucher to specific ticket categories (season members only, VIP tier only, over-18 seat blocks) so licensing officers stay happy. Unspent balance is either carried across events (default) or auto-refunded within 21 days at your discretion. It's the fastest wallet-adoption lever we've measured — turning a 4% top-up rate on match day into a 60%+ pre-load rate for the same crowd.",
  },
  { q: "Do I need new hardware?",
    a: "No. Every stall runs on any Android or iOS phone / tablet already on-site. If you want dedicated kitchen printers, we integrate with the same Star Micronics / Epson TM-m30 line HustlePlate uses." },
  { q: "How do split-payments work under Stripe's rules?",
    a: "We use Stripe Connect Custom accounts per venue. Each split-pay participant pays via a separate PaymentIntent linked to the same order — so refunds, chargebacks and payouts all attribute cleanly. No shared card details, no PCI headache." },
  { q: "What about drinks / RSA compliance?",
    a: "Every alcohol line item requires the fan to complete an in-app age gate before ordering. Runners scan the fan's ID at seat-side delivery. Full RSA audit trail exported to your compliance team weekly." },
  { q: "Can it run offline if the stadium Wi-Fi dies?",
    a: "The fan app queues orders on-device and syncs when connectivity returns. The stall app runs a local-first order queue against IndexedDB. Payouts settle once the transaction posts back to Stripe." },
  { q: "How does this play with HustleTickets?",
    a: "Every ticket sold via HustleTickets ships with a pre-linked HustleStadium wallet — fans can prepay their match-day food during ticket checkout. One-click cross-sell." },
  { q: "How's this different from Squadron / SpotOn / Toast?",
    a: "Three ways: (1) group split-pay up to 12 is built in, no add-on; (2) HustlePoints work as a wallet currency across every stall AND every HustleSuite venue; (3) the same platform runs your restaurant business (HustlePlate) and your discoverability (HustleDirectory) — no ETL between systems." },
  { q: "How does RSA compliance work at multi-day festivals?",
    a: "Every alcohol line item is gated by an in-app age-verification wristband. Punters complete an ID scan at the front gate (linked to their HustleStadium wallet). The wristband state — verified, drinks-allocated, cut-off after RSA hours — is synced live to every stall and every roving vendor. Bar staff can see a red-flag banner on the point-of-sale the moment a wristband exceeds standard-drinks per hour. Full RSA audit trail is exportable to your licensing officer weekly. Same rules for pop-up bars at pop-up festivals." },
  { q: "Can HustleStadium actually handle 50-100k patrons at a Bathurst or Splendour scale?",
    a: "Yes. The platform is engineered on horizontally-scaling Stripe Connect + Firestore-style order queues that we've stress-tested to 20,000 concurrent open baskets and 3,500 order-completions per minute. For Bathurst-scale weekends we set up a dedicated on-site Wi-Fi mesh (or hook into your existing infrastructure) and pre-provision extra Stripe payment-intent capacity. For festival-scale multi-day events we deploy an on-site engineer for the first 24 hours to hand-hold the kitchen ops team through peak throughput. This is included in the Season Pass / Enterprise tier — you'll never see a payment queue timeout under normal Australian mobile-carrier conditions." },
];

function StatPill({ num, label }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "baseline", gap: 6,
      padding: "8px 14px", borderRadius: 999,
      background: "#fff", border: "1px solid #ecdeb7",
      boxShadow: "0 4px 10px rgba(0,0,0,.05)",
    }}>
      <strong style={{ fontFamily: "Georgia,serif", fontSize: 20, color: "#1a1408" }}>{num}</strong>
      <span style={{ fontSize: 13, color: "#5a4a1a" }}>{label}</span>
    </div>
  );
}

function TierCard({ tier }) {
  return (
    <div data-testid={`stadium-tier-${tier.key}`}
         style={{
           background: "#fff", borderRadius: 18, padding: "26px 24px",
           border: tier.highlight ? "2px solid #b08a3e" : "1px solid #ecdeb7",
           boxShadow: tier.highlight ? "0 20px 44px rgba(176,138,62,.22)" : "0 8px 20px rgba(0,0,0,.06)",
           position: "relative", display: "flex", flexDirection: "column", gap: 14,
         }}>
      {tier.badge && (
        <span style={{
          position: "absolute", top: -12, right: 18,
          background: "linear-gradient(135deg,#1f1f1f 0%,#4a3b1f 50%,#b08a3e 100%)",
          color: "#fff", fontSize: 11, fontWeight: 800,
          padding: "5px 12px", borderRadius: 999, letterSpacing: ".05em",
        }}>{tier.badge}</span>
      )}
      <div>
        <div style={{ color: tier.color, fontWeight: 800, letterSpacing: ".12em",
                       fontSize: 12, textTransform: "uppercase" }}>
          {tier.name}
        </div>
        <div style={{ marginTop: 6, fontFamily: "Georgia,serif",
                       fontSize: 36, color: "#1a1408" }}>
          {tier.price}
          <span style={{ fontSize: 15, color: "#8a7a4a", marginLeft: 4 }}>{tier.period}</span>
        </div>
      </div>
      <p style={{ color: "#5a4a1a", lineHeight: 1.55, fontSize: 14, margin: 0 }}>
        {tier.blurb}
      </p>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
        {tier.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 8, fontSize: 14, color: "#1a1408", lineHeight: 1.45 }}>
            <Check size={16} color={tier.color} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={tier.cta.href} data-testid={`stadium-tier-${tier.key}-cta`}
            style={{ ...ctaPrimaryStyle, alignSelf: "flex-start", marginTop: 4,
                       background: tier.highlight ? "#b08a3e" : "#c2410c",
                       boxShadow: tier.highlight
                         ? "0 8px 20px rgba(176,138,62,.32)"
                         : "0 8px 20px rgba(194,65,12,.22)" }}>
        {tier.cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

export default function HustleStadiumSalesPage() {
  useEffect(() => {
    document.title = "HustleStadium — Stall-menu QR ordering + group split-pay for stadiums";
  }, []);

  return (
    <div data-testid="hustlestadium-sales-page" className="min-h-screen"
         style={{ background: "#fdf9ec", color: "#1a1408" }}>
      <PlatformHeader salesTopGap
        brand={{ name: "HustleStadium", to: "/" }}
        nav={[
          { to: "#stadium-hero", label: "Overview" },
          { to: "#tiers", label: "Pricing" },
          { to: "/claim-business?product=stadium&tier=trial", label: "Book a demo match day", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 80px" }}>

        {/* Hero */}
        <section id="stadium-hero" data-testid="stadium-hero" style={{ padding: "40px 0 24px", scrollMarginTop: 80 }}>
          <img src={LOGO} alt="HustleStadium" style={{ height: 52, marginBottom: 14 }} />
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 10px" }}>
            HustleStadium · match-day food ops
          </p>
          <h1 style={{ fontFamily: "Georgia,serif", fontSize: 46, lineHeight: 1.1,
                        color: "#1a1408", margin: "0 0 14px", maxWidth: 900 }}>
            Stall-menu QR ordering, group split-pay, and a live venue map —{" "}
            <em style={{ color: "#b08a3e", fontStyle: "italic" }}>on any</em>{" "}
            stadium, arena or festival.
          </h1>
          <p style={{ fontSize: 17, color: "#5a4a1a", maxWidth: 820,
                       margin: "0 0 20px", lineHeight: 1.55 }}>
            HustleStadium is the food, drink and merch operating layer for
            large-scale venues. Fans <strong>pre-order from stall menus</strong>,{" "}
            <strong>pick up on the concourse</strong> (or claim seat-side delivery),
            and <strong>split-pay a group order</strong> across up to twelve wallets.
            You get a real-time venue map, live queue data on every stall, and
            every transaction reconciled against Stripe — no cash tin at the door.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
            <StatPill num="12" label="max split-pay wallets" />
            <StatPill num="~9%" label="stall-margin uplift" />
            <StatPill num="T+0" label="Stripe payout (Platinum)" />
            <StatPill num="RSA" label="alcohol audit trail" />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/claim-business?product=stadium&tier=trial"
                  data-testid="stadium-cta-trial" style={ctaPrimaryStyle}>
              Book a demo match day <ArrowRight size={16} />
            </Link>
            <a href="#tiers" data-testid="stadium-cta-pricing" style={ctaSecondaryStyle}>
              <Wallet size={16} /> See pricing
            </a>
            <button data-testid="stadium-cta-print"
                    onClick={() => window.print()}
                    style={{ ...ctaSecondaryStyle, cursor: "pointer" }}>
              <Printer size={16} /> Print proposal
            </button>
          </div>
        </section>

        {/* Video Loop — silent 30s highlight reel above Chapter 1 */}
        <section id="stadium-video" data-testid="stadium-video-loop"
                 style={{ marginTop: 40, marginBottom: 16, scrollMarginTop: 80,
                           borderRadius: 20, overflow: "hidden",
                           border: "1px solid #ecdeb7",
                           boxShadow: "0 20px 44px rgba(0,0,0,.10)",
                           background: "#0e0e10" }}>
          <video
            data-testid="stadium-video-loop-el"
            autoPlay muted loop playsInline
            preload="metadata"
            poster="https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=1600&q=80&auto=format&fit=crop"
            style={{ display: "block", width: "100%", height: "auto",
                       maxHeight: 380, objectFit: "cover" }}
          >
            {/* Placeholder — swap the src to a self-hosted 30s HustleStadium
                highlight-reel MP4 (silent, H.264, 1080p, ≤ 8 MB) at
                /assets/stadium-hero-loop.mp4 once produced. */}
            <source src="https://cdn.coverr.co/videos/coverr-stadium-crowd-cheering-9027/1080p.mp4"
                    type="video/mp4" />
          </video>
        </section>

        {/* Capabilities */}
        <section style={{ marginTop: 64 }} data-testid="stadium-caps">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 1 · what ships in the box
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Eight things that make match-day food ops actually run.
          </h2>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {CAPS.map((c, i) => (
              <div key={i} data-testid={`stadium-cap-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <c.icon size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{c.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.5, margin: 0 }}>{c.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 3-step how it works */}
        <section style={{ marginTop: 72 }} data-testid="stadium-how">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 2 · live before your next home game
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Three steps. Roughly a week.
          </h2>
          <div style={{ display: "grid", gap: 18,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {HOW.map((s) => (
              <div key={s.n} data-testid={`stadium-step-${s.n}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "24px 24px",
                              border: "1px solid #ecdeb7",
                              display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: "Georgia,serif", fontSize: 34,
                                    color: "#b08a3e", lineHeight: 1 }}>{s.n}</span>
                  <s.icon size={20} color="#c2410c" />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 20,
                              color: "#1a1408", margin: 0 }}>{s.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Chapter 2.5 — VIP dining + numbered viewing decks */}
        <section style={{ marginTop: 72 }} data-testid="stadium-vip">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 2.5 · VIP dining + numbered viewing decks
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 14px" }}>
            The event's dedicated VIP restaurant — with real table &amp; seat numbers.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 900, lineHeight: 1.65, margin: "0 0 24px" }}>
            Every major venue has premium hospitality: the corporate box on the
            50-yard line, the trackside chef's-table restaurant, the balcony
            viewing deck above the stage, the sky lounge that runs its own
            à-la-carte menu. Until now, none of that lived on the same
            operating layer as the concourse stalls — so guests juggled
            three apps, waiters juggled paper dockets, and reconciliation
            was a Monday-morning migraine. <strong>HustleStadium ships a
            first-class VIP module</strong> that treats these premium
            spaces exactly like a HustlePlate restaurant floor plan:
            drag-drop tables, numbered seats, hold and pre-order windows,
            table-side split-pay and one Stripe payout with the rest of
            match-day. One stop shop, one wallet, one dashboard.
          </p>

          <div style={{ display: "grid", gap: 20, marginTop: 8,
                         gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {[
              { icon: Utensils, t: "Dedicated VIP restaurant floor plan",
                b: "Import the corporate box / trackside restaurant / rooftop bar layout via HustlePlate's Table Builder. Round tables of eight, chef's-counter twelves, sky-lounge lounges — every table gets a persistent number the whole season." },
              { icon: MapPin,   t: "Numbered viewing-deck seats",
                b: "The Wallabies box, the finish-line balcony, the theatre dress circle. Every seat carries an identifier the same way general admission does — venue managers see the exact block, row and seat number on every order slip." },
              { icon: QrCode,   t: "One QR per table, one per seat",
                b: "Guests scan the table QR to order for the whole booking, or scan their own seat QR for a private tab. Runners deliver seat-side using the numbered seat map — no more shouting 'section 4, corner of the couch, second guy'." },
              { icon: Users,    t: "Auto-invite every guest to split-pay",
                b: "The lead-booker's ticket seeds the split-pay group with every attendee on the VIP booking. Everyone lands with their share pre-loaded, Apple Pay or HustlePoints in one tap, and lead-booker chargebacks are impossible." },
              { icon: Timer,    t: "Pre-order for interval, halftime, encore",
                b: "Set fixed pickup or delivery windows anchored to the match clock, the intermission break or the encore. VIP dining lands warm, on time, and without a queue — because kitchen prep timing is driven by the schedule, not by ticks off a dessert docket." },
              { icon: HandCoins, t: "One Stripe payout, one dashboard",
                b: "VIP transactions land on the same Stripe Connect account as concourse orders. Same T+0 payout on Platinum, same reconciliation report, same tax export. Your finance team stops writing spreadsheets by lunch on Monday." },
            ].map((v, i) => (
              <div key={i} data-testid={`stadium-vip-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <v.icon size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{v.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{v.b}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 28, background: "#fff8e6",
                         border: "1px solid #ecdeb7", borderRadius: 16,
                         padding: "22px 24px", display: "flex", gap: 16,
                         alignItems: "flex-start", flexWrap: "wrap" }}
               data-testid="stadium-vip-callout">
            <Award size={36} color="#b08a3e" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 260 }}>
              <h3 style={{ fontFamily: "Georgia,serif", fontSize: 22,
                            color: "#1a1408", margin: "0 0 8px" }}>
                Because HustleStadium is now a full one-stop shop.
              </h3>
              <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>
                General admission on the concourse, corporate hospitality
                in the VIP restaurant, and numbered viewing decks upstairs —
                all on the same platform, one login for the guest, one
                dashboard for the ops team, one Stripe payout for finance.
                No bolt-ons, no separate POS, no rebuilding the wheel when
                your Platinum members expect a real dining experience.
              </p>
            </div>
          </div>

          {/* Live VIP booking widget — pulls availability from
              /api/stadium/vip-availability. */}
          <div style={{ marginTop: 28 }} data-testid="stadium-vip-widget-mount">
            <VipBookingWidget />
          </div>
        </section>

        {/* Chapter 2.6 — Wallet top-up at ticket checkout */}
        <section style={{ marginTop: 72 }} data-testid="stadium-wallet-topup">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 2.6 · match-day wallet top-up
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 14px" }}>
            Pre-load the wallet during ticket checkout — kill the gate-day ATM queue.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 900, lineHeight: 1.65, margin: "0 0 24px" }}>
            Every HustleStadium wallet is provisioned the moment a
            ticket is scanned. But the real trick is topping it up
            <em> before</em> match day: our checkout add-on drops onto
            Ticketek, Ticketmaster, Moshtix and (natively) HustleTickets,
            so fans slide a $50 / $100 / $200 wallet load into the same
            basket as their ticket. Promo codes, first-drink vouchers
            and RSA-safe per-fan caps are configured in your operator
            console. On match day the fan skips the ATM, opens their
            wallet at the food stall, and pays in one tap — you skip
            an entire class of gate infrastructure.
          </p>

          <div style={{ display: "grid", gap: 20, marginTop: 8,
                         gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {[
              { icon: Ticket,   t: "Drops onto every major ticket rail",
                b: "Native integrations with Ticketek, Ticketmaster, Moshtix and HustleTickets. The wallet top-up appears alongside seat pick + parking + merch as a single line item." },
              { icon: HandCoins, t: "Promo credits + first-drink vouchers",
                b: "Configure MATCHDAY10, FIRSTLOAD or your own promo codes with per-fan match-day caps. Add a $12 first-drink voucher automatically on qualifying top-ups — set the trigger in the operator console." },
              { icon: Wallet,   t: "One Stripe hold, one wallet balance",
                b: "The card is charged once; the balance appears in HustlePoints. Refunds, unspent-balance settlements and unclaimed-voucher expiry are all reconciled through Stripe Connect on the same T+0 payout as concourse orders (Platinum tier)." },
              { icon: BarChart3, t: "Fewer ATMs, faster gate lines",
                b: "A pre-loaded wallet is 4x faster at the food stall tap than a card swipe, and there's no ATM queue at the gates. Your ops team's biggest crowd-flow bottleneck simply disappears from the match-day plan." },
            ].map((v, i) => (
              <div key={i} data-testid={`stadium-topup-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <v.icon size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{v.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{v.b}</p>
              </div>
            ))}
          </div>

          {/* Live top-up preview widget */}
          <div style={{ marginTop: 28 }} data-testid="stadium-topup-widget-mount">
            <StadiumWalletTopupWidget />
          </div>
        </section>

        {/* Chapter 3 — Beyond the whistle: every venue type */}
        <section style={{ marginTop: 72 }} data-testid="stadium-venues">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 3 · beyond the whistle
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 14px" }}>
            Markets, festivals, tennis, surfing — every venue-scale event.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 900, lineHeight: 1.65, margin: "0 0 28px" }}>
            HustleStadium wasn't built for one code of football. It runs the food,
            drink, merch and ticket-scan layer for every large-scale, ticketed or
            gated event in Australia — from a Sunday farmers' market with twelve
            stalls, to a 90,000-seat MCG grand-final, to a beach-side surf carnival
            with roving vendors on the sand. The engine is the same: printable
            stall QR codes, a live venue map, group split-pay up to twelve wallets,
            RSA-compliant alcohol ordering, and every transaction reconciled through
            Stripe Connect. What changes is the venue skin — the map layer, the
            payout schedule, the RSA rules and the pickup windows are all templated
            per event type, so a suburban Rotary market and a two-day music festival
            each run on defaults that already understand their compliance surface.
            One platform, every venue type, zero rebuilds when your event grows.
          </p>
          <img src="https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1400&q=80&auto=format&fit=crop"
               alt="Music festival crowd" data-testid="stadium-venues-hero-img"
               style={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 18,
                        border: "1px solid #ecdeb7", boxShadow: "0 20px 44px rgba(0,0,0,.08)",
                        marginBottom: 28 }} />
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {[
              { t: "Markets & artisan fairs",
                b: "Weekend farmers' markets, night markets, Christmas markets, EOFY handmade fairs. Every stall gets a printable QR + a portable menu. Punters pre-order coffee at the entrance and pick up two stalls later — zero queue, zero missed sales during the 20-minute morning rush. Vendor payout drops the same evening via Stripe Instant." },
              { t: "Music festivals",
                b: "Splendour, Falls, Bluesfest-scale multi-day events or one-night gigs. RSA-compliant alcohol wristband gating, group split-pay across twelve mates in one campsite, and a live map showing which food truck sold out of hot chips at 9pm. Optional in-app top-up before entry so there are no ATM queues at the front gate." },
              { t: "Tennis & racquet events",
                b: "AO qualifying weeks, Brisbane International, regional tennis opens, pickleball tournaments. Every court gets its own on-court QR pack; spectators order between changeovers and collect on a scheduled 15-minute pickup window that syncs to the match timer. Perfect for centre-court hospitality and the outer courts alike." },
              { t: "Surfing & beach carnivals",
                b: "WSL events, Nippers carnivals, surf-lifesaving competitions. Roving-vendor mode (no fixed stall) lets a beach-side ute run the whole ordering flow off a tablet. GPS-verified pickup points so patrons find the mobile vendor even after it moves 300m up the beach with the tide." },
              { t: "Motorsport & speedway",
                b: "Bathurst, Supercars, Superbikes, local speedway meets. Big-crowd throughput (50-100k), pit-side runner mode for pit-lane hospitality, and a Stripe payout schedule that matches your race weekend cadence — cash cleared into your account before the pack files past the chequered flag on Sunday." },
              { t: "Comedy, theatre, touring shows",
                b: "Sydney Opera House to a regional town hall — HustleStadium handles interval bar rushes, pre-show snack pre-orders, encore-time merch and cast-and-crew meal orders on the same platform. Print-friendly seat-map QR codes so audiences find their code with the house lights down." },
            ].map((v, i) => (
              <div key={i} data-testid={`stadium-venue-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <Sparkles size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{v.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{v.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Chapter 4 — Ticketing integrations */}
        <section style={{ marginTop: 72 }} data-testid="stadium-ticketing">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 4 · ticketing integrations
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 14px" }}>
            One wallet across every ticket partner.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 900, lineHeight: 1.65, margin: "0 0 28px" }}>
            HustleStadium ships with native integrations to every major Australian
            ticketing rail. The moment a fan buys a ticket to your venue via any
            supported partner, a pre-linked HustleStadium wallet is provisioned
            against their seat, ready to load pre-orders or split-pay a group tab.
            No re-registration, no card re-entry, no double-up of the customer
            identity. If your venue also runs HustleTickets — our native ticketing
            rail — the whole flow becomes a single checkout: ticket + parking +
            first drink + merch, one card, one basket. Refund reconciliation
            flows back through Stripe Connect within sixty minutes across every
            partner, so cancellations don't leave orphaned pre-orders sitting on
            the kitchen line.
          </p>
          <img src="https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1400&q=80&auto=format&fit=crop"
               alt="Concert ticketing" data-testid="stadium-ticketing-hero-img"
               style={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 18,
                        border: "1px solid #ecdeb7", boxShadow: "0 20px 44px rgba(0,0,0,.08)",
                        marginBottom: 28 }} />
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
            {[
              { t: "Ticketek",
                b: "The largest Australian ticketing partner. HustleStadium consumes the Ticketek Barcode API to pre-load each ticket-holder's wallet at the point of scan. Refund reconciliation flows back through Stripe Connect within sixty minutes; last-minute name-changes reflect within ninety seconds." },
              { t: "Ticketmaster",
                b: "Live Nation's global ticketing rail. We support both TM+ resale and primary sale barcodes. Group tickets purchased under one lead-booker account are auto-linked so the buyer can invite friends into a group split-pay tab without collecting names — perfect for corporate boxes and party hire." },
              { t: "Moshtix",
                b: "The default rail for Aussie festivals, comedy tours and mid-tier venues. Moshtix's Access Manager API keeps our wallet-provisioning in sync with the door-list — cancellations, upgrades and last-minute name-changes all reflect within ninety seconds. RSA wristband states are pushed both ways." },
              { t: "HustleTickets",
                b: "Our native ticketing rail. Zero-commission on repeat visits, unified customer identity, and a single checkout for ticket + food + parking + merch. If you're launching a new venue or running a private-tenanted event, this is the fastest path to live — usually one afternoon of setup for a full match-day rollout." },
            ].map((tp, i) => (
              <div key={i} data-testid={`stadium-ticketing-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <Ticket size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{tp.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{tp.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Chapter 4.5 — Checkout-bridge SDK + venue ops onboarding */}
        <section style={{ marginTop: 72 }} data-testid="stadium-bridge-sdk">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 4.5 · checkout-bridge SDK + venue onboarding
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 14px" }}>
            Two lines of HTML on your ticket page. One afternoon to onboard.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 900, lineHeight: 1.65, margin: "0 0 24px" }}>
            The checkout-bridge SDK is a single <code>&lt;script&gt;</code>
            tag your ticket rail — Ticketek, Ticketmaster, Moshtix or
            HustleTickets — drops onto the confirmation step. It renders
            the same wallet top-up widget your fans just saw above, with
            promo codes, first-drink vouchers and one-tap load-to-wallet.
            When they confirm, the fan's HustleStadium wallet is minted
            and pre-loaded against the seat they just bought — no
            re-registration, no card re-entry, no gate-day ATM queue.
            Meanwhile, your venue ops team uploads the floor-plan CSV
            through the console and every widget on this page starts
            pointing at your real match-day layout.
          </p>

          <div style={{ display: "grid", gap: 22,
                         gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))" }}>

            {/* Left card — Embed snippet */}
            <div data-testid="stadium-bridge-embed"
                 style={{ background: "#1a1408", color: "#fff8e6",
                            borderRadius: 16, padding: "22px 22px",
                            border: "1px solid #4a3b1f",
                            boxShadow: "0 20px 44px rgba(0,0,0,.14)" }}>
              <div style={{ fontSize: 11, letterSpacing: ".2em",
                              textTransform: "uppercase",
                              color: "#e6b649", fontWeight: 700,
                              marginBottom: 10 }}>
                Two lines of HTML
              </div>
              <pre style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6,
                              overflowX: "auto", color: "#fff8e6" }}>
{`<div id="hs-wallet"></div>
<script src="https://hustlestadium.au/api/stadium/checkout-bridge/embed.js"></script>
<script>
  HustleStadiumBridge.render({
    mount:      "#hs-wallet",
    partner:    "ticketek",       // or ticketmaster / moshtix / hustletickets
    order_ref:  "TKT-193222",
    venue:      "suncorp-stadium",
    event_date: "2026-03-14",
    fan_email:  "chloe@example.com",
    onConfirm: (txn) => console.log("wallet loaded", txn),
  });
</script>`}
              </pre>
              <p style={{ marginTop: 14, fontSize: 12,
                            color: "#d4b060", lineHeight: 1.5 }}>
                Full documented API at{" "}
                <code style={{ color:"#fff" }}>
                  /api/stadium/checkout-bridge/embed.js
                </code>. Loads asynchronously, no CSS collisions, and
                degrades gracefully if the ticket page blocks
                third-party scripts.
              </p>
            </div>

            {/* Right card — Venue Ops CTA */}
            <div data-testid="stadium-ops-cta"
                 style={{ background: "#fff", color: "#1a1408",
                            borderRadius: 16, padding: "22px 22px",
                            border: "1px solid #ecdeb7",
                            boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
              <div style={{ fontSize: 11, letterSpacing: ".2em",
                              textTransform: "uppercase",
                              color: "#b08a3e", fontWeight: 700,
                              marginBottom: 10 }}>
                Venue onboarding — one afternoon
              </div>
              <h3 style={{ fontFamily: "Georgia,serif",
                             fontSize: 22, margin: "0 0 10px" }}>
                Upload your floor plan. Go live.
              </h3>
              <p style={{ fontSize: 14, color: "#5a4a1a",
                            lineHeight: 1.55, margin: "0 0 12px" }}>
                Every venue on HustleStadium can bring their own floor
                plan via a single CSV. Every table gets a persistent
                number, every seat gets a block-row-seat identifier,
                and every widget on this page updates the moment you
                click "Save". Ops team emails on the venue are
                automatically CC'd on every new inquiry (via Resend).
              </p>
              <ul style={{ margin: "8px 0 14px", paddingLeft: 18,
                             fontSize: 13, color: "#3a2f18",
                             lineHeight: 1.6 }}>
                <li>CSV columns: <code>zone_id, zone_name, type, table_no, seats_from, seats_to, price_pp, available</code></li>
                <li>Zone types: <code>restaurant</code>, <code>box</code>, <code>viewing-deck</code></li>
                <li>Ops emails: comma-separated — every new VIP inquiry goes to them <em>and</em> to the founder inbox</li>
              </ul>
              <a href="/stadium-ops" data-testid="stadium-ops-console-link"
                 style={{ display: "inline-flex", alignItems: "center", gap: 8,
                            padding: "11px 20px", borderRadius: 999,
                            background: "#c2410c", color: "#fff",
                            textDecoration: "none", fontWeight: 700,
                            fontSize: 14 }}>
                Open the ops console <ArrowRight size={15} />
              </a>
            </div>
          </div>
        </section>

        {/* Tiers */}
        <section id="tiers" style={{ marginTop: 72, scrollMarginTop: 80 }} data-testid="stadium-tiers">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 5 · pricing
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 10px" }}>
            Match-day trial. Platinum season. Enterprise for the big four.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 820, lineHeight: 1.55, margin: "0 0 28px" }}>
            Every plan is billed against a single unified subscription —
            <strong> HustlePlatinum</strong> — so upgrading also lights up
            the Featured badge across HustleDirectory, HustlePlaza and
            HustleTravel. One Stripe subscription, ten module unlocks.
          </p>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>
            {TIERS.map((t) => <TierCard key={t.key} tier={t} />)}
          </div>
        </section>

        {/* Trust callout */}
        <section style={{ marginTop: 72 }} data-testid="stadium-trust">
          <div style={{ background: "#fff8e6", borderRadius: 18,
                         border: "1px solid #ecdeb7", padding: "28px 30px",
                         display: "flex", gap: 20, flexWrap: "wrap",
                         alignItems: "flex-start" }}>
            <Sparkles size={40} color="#b08a3e" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 260 }}>
              <h3 style={{ fontFamily: "Georgia,serif", fontSize: 24,
                            color: "#1a1408", margin: "0 0 8px" }}>
                One platform. Every match day.
              </h3>
              <p style={{ fontSize: 15, color: "#5a4a1a", lineHeight: 1.55,
                           margin: "0 0 10px" }}>
                HustleStadium is not a bolt-on. It ships with the same
                unified customer identity, unified wallet (HustlePoints),
                unified admin console and unified Stripe Connect that
                power every other HustleSuite module. If a fan is
                already a HustlePlate diner, a HustleTravel member, or a
                HustleDirectory reviewer, they land at their seat with
                one login and one wallet.
              </p>
              <p style={{ fontSize: 15, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>
                And if you ever decide to leave, your data goes with you:
                seat plan, menus, fan wallets, transaction history — all
                exported to CSV or handed over to your next platform on
                request. Same rules as every other HustleSuite module.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ marginTop: 72 }} data-testid="stadium-faq">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 6 · honest FAQ
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Questions venue managers actually ask.
          </h2>
          <div style={{ display: "grid", gap: 12 }}>
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`stadium-faq-${i}`}
                       style={{ background: "#fff", borderRadius: 12,
                                  border: "1px solid #ecdeb7", padding: "16px 20px" }}>
                <summary style={{ fontFamily: "Georgia,serif", fontSize: 17,
                                    color: "#1a1408", cursor: "pointer",
                                    fontWeight: 600 }}>
                  {f.q}
                </summary>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55,
                             margin: "10px 0 0" }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section style={{ marginTop: 72 }} data-testid="stadium-final-cta">
          <div style={{
            background: "linear-gradient(135deg,#1a1408 0%,#4a3b1f 55%,#b08a3e 100%)",
            color: "#fff", padding: "40px 36px", borderRadius: 22,
            display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center",
          }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <h2 style={{ fontFamily: "Georgia,serif", fontSize: 30, margin: "0 0 8px" }}>
                Book a demo match day. No card.
              </h2>
              <p style={{ fontSize: 15, opacity: .9, margin: 0, lineHeight: 1.55 }}>
                We stand up the whole stack on one game, one show, or one
                festival day. If you don't want to keep it, we walk away
                and hand back the data.
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/claim-business?product=stadium&tier=trial"
                    data-testid="stadium-final-trial"
                    style={{ ...ctaPrimaryStyle, background: "#fff", color: "#1a1408",
                              boxShadow: "0 10px 30px rgba(0,0,0,.28)" }}>
                Book a demo match day <ArrowRight size={16} />
              </Link>
              <Link to="/claim-business?product=stadium&tier=platinum"
                    data-testid="stadium-final-platinum"
                    style={{ ...ctaSecondaryStyle, borderColor: "#fff", color: "#fff" }}>
                <Building2 size={16} /> Talk about Platinum
              </Link>
            </div>
          </div>
        </section>
      </main>

      <FounderChatPill testid="stadium-founder-chat" />
      <SuiteFooter />
    </div>
  );
}
